//@flow
import React, {useRef, useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import {Button, Snackbar} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import {colors} from '../../../../../assets/styles/colors';
import {getImage} from '../../../../../utils/image';
import Alert from '@material-ui/lab/Alert';

type Props = {|
  backgroundImage: string,
  logoImage?: string,
  logo: {
    position: number[],
    maxSize: number[],
  },
  domain: {
    position: number[],
    style: {[keys: string]: any},
    maxLength: number,
    newFontSize: number,
    space: number,
  },
  domainText: string,
  size: number[],
|};

const useStyles = makeStyles(theme => ({
  button: {
    textTransform: 'initial',
    color: colors.grey800,
  },
  wrapCanvas: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: colors.grey200,
    borderRadius: '12px',
  },
  root: {
    margin: 'auto',
    width: 'fit-content',
  },
  wrapButtons: {
    paddingBottom: theme.spacing(1),
  },
  canvas: {
    padding: '12px',
    maxWidth: props => `${props.size[0]}px`,
    height: props => `${props.size[1]}px`,
  },
}));

const RATIO = 4;

export default function CanvasImage(props: Props) {
  const {backgroundImage, logoImage, logo, domainText, domain, size} = props;
  const classes = useStyles(props);
  const canvasRef = useRef(null);
  const buttonRef = useRef(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [widthLogo, setWidthLogo] = useState(0);
  const [heightLogo, setHeightLogo] = useState(0);
  const [loadCanvas, setLoadCanvas] = useState(false);

  const writeText = (
    info: {text: string, x: number, y: number},
    style: {[key: string]: string}
  ) => {
    const {text, x, y} = info;
    const {
      fontSize = 20,
      fontFamily = 'Arial',
      color = 'black',
      textAlign = 'left',
      textBaseline = 'top',
    } = style;

    const canvas = canvasRef.current;

    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.scale(1, 1);
      ctx.beginPath();
      ctx.font = fontSize + 'px ' + fontFamily;
      ctx.textAlign = textAlign;
      ctx.textBaseline = textBaseline;
      ctx.fillStyle = color;
      ctx.fillText(text, x, y);
      ctx.stroke();
    }
  };

  const centerLogo = (
    logo: {
      position: number[],
      maxSize: number[],
    },
    width: number,
    height: number
  ) => {
    const proportion = width / height;
    if (proportion > 0) {
      if (logo.maxSize[0] / proportion > logo.maxSize[1]) {
        setHeightLogo(logo.maxSize[1]);
        setWidthLogo(logo.maxSize[1] * proportion);
      } else {
        setWidthLogo(logo.maxSize[0]);
        setHeightLogo(logo.maxSize[0] / proportion);
      }
    } else {
      if (logo.maxSize[1] * proportion <= logo.maxSize[0]) {
        setWidthLogo(logo.maxSize[0]);
        setHeightLogo(logo.maxSize[0] / proportion);
      } else {
        setHeightLogo(logo.maxSize[1]);
        setWidthLogo(logo.maxSize[1] * proportion);
      }
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;

    //LOAD BACKGROUND
    if (canvas && logoImage) {
      const ctx = canvas.getContext('2d');
      ctx.scale(1, 1);
      getImage(backgroundImage).then(({image, width, height}) => {
        ctx.drawImage(image, 0, 0, size[0] * RATIO, size[1] * RATIO);

        //LOAD TEXT
        let style = domain.style;
        let domainPosition = [];
        let arrayText = [];
        arrayText.push(domainText);
        domainPosition.push([0, 0]);
        if (domainText.length > domain.maxLength) {
          domainPosition.push([0, 0]);
          style.fontSize = domain.newFontSize;

          domainPosition[0][1] = domain.position[1] - domain.space;
          domainPosition[1][1] = domain.position[1] + domain.space;
          domainPosition[0][0] = domain.position[0] - 3;
          domainPosition[1][0] = domain.position[0] + 3;

          arrayText[0] = arrayText[0].replace('tiendada.com', '');
          arrayText.push('tiendada.com');
        } else {
          domainPosition[0][0] = domain.position[0];
          domainPosition[0][1] = domain.position[1];
        }

        arrayText.map((item: string, index: number) => {
          return writeText(
            {
              text: item,
              x: domainPosition[index][0] * RATIO,
              y: domainPosition[index][1] * RATIO,
            },
            style
          );
        });

        //LOAD LOGO
        getImage(logoImage).then(({image, width, height}) => {
          centerLogo(logo, width, height);
          ctx.drawImage(
            image,
            (logo.position[0] + (logo.maxSize[0] - widthLogo) / 2) * RATIO,
            (logo.position[1] + (logo.maxSize[1] - heightLogo) / 2) * RATIO,
            widthLogo * RATIO,
            heightLogo * RATIO
          );
        });
      });
      setLoadCanvas(true);
    }
  }, [
    backgroundImage,
    domain,
    domainText,
    heightLogo,
    logo,
    logoImage,
    size,
    widthLogo,
  ]);

  const handleDowload = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      var dataURL = canvas.toDataURL('image/jpeg', 1.0);
      const button = buttonRef.current;
      if (button) button.href = dataURL;
      setAlertOpen(true);
    }
  };

  return (
    <div className={classes.root}>
      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={() => setAlertOpen(false)}
      >
        <Alert severity="success" onClose={() => setAlertOpen(false)}>
          Imagen descargada
        </Alert>
      </Snackbar>
      <div className={classes.wrapCanvas}>
        <canvas
          ref={canvasRef}
          width={size[0] * RATIO}
          height={size[1] * RATIO}
          className={classes.canvas}
        />
        <div className={classes.wrapButtons}>
          {loadCanvas && (
            <Button
              ref={buttonRef}
              size="large"
              download="marketing-canvas.jpeg"
              onClick={handleDowload}
              href="#"
              className={classes.button}
              startIcon={<GetAppIcon />}
            >
              Descargar
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

CanvasImage.propTypes = {
  backgroundImage: PropTypes.string.isRequired,
  logoImage: PropTypes.string,
  domainText: PropTypes.string.isRequired,
  logo: PropTypes.object.isRequired,
  domain: PropTypes.object.isRequired,
  size: PropTypes.array.isRequired,
};
